/**
 * COMPROVANTE DE ENVIO da fatura: o que foi mandado, não só para quem.
 * Prévia: https://claude.ai/artifact/2Sd2oaSpt9p37c6nw45UoZ · backend #293
 * (`GET /invoices/:id/comprovantes-de-envio`, forma em helpers/comprovante-de-envio.util.ts).
 *
 * ⛔ ENVIO ANTERIOR AO COMPROVANTE. Os 3.880 envios anteriores à tabela não têm
 * corpo nem anexos, e a API devolve `semComprovante` / `registrado: false`. A tela
 * diz isso com todas as letras: painel vazio com cara de defeito é pior que a
 * verdade, e reconstruir pelo modelo atual faria o comprovante mentir.
 *
 * ⛔ ANEXO É REFERÊNCIA, NÃO CÓPIA. Os bytes não foram guardados. Onde o arquivo
 * ainda existe do nosso lado, o nome abre o documento vivo; onde não existe, fica o
 * registro de que existiu, com tamanho e data. O TAMANHO ORIGINAL foi gravado de
 * propósito: divergência denuncia que o arquivo mudou depois do envio.
 */

export type OrigemDoAnexo =
  | { tipo: 's3'; s3Key: string }
  | { tipo: 'documento_fiscal'; documentoId: number; s3Key?: string }
  | { tipo: 'gerado_no_envio' };

export interface AnexoDoComprovante {
  nome: string;
  tipo: string;
  /** Bytes como foi ENVIADO. */
  tamanho: number;
  origem: OrigemDoAnexo;
  /** Estado de hoje, quando o backend o informa (aditivo; ausente em versões antigas). */
  atual?: { existe: boolean; tamanho?: number | null } | null;
}

export interface Comprovante {
  registrado: boolean;
  motivo?: string;
  id?: number;
  enviadoEm?: string;
  enviadoPor?: string | null;
  caminho?: string;
  assunto?: string;
  corpoHtml?: string;
  notaDoOperador?: string | null;
  modeloDeEntrega?: string | null;
  destinatarios?: string[];
  anexos?: AnexoDoComprovante[];
}

export interface RespostaDosComprovantes {
  invoiceId: number;
  total: number;
  comprovantes: Comprovante[];
  semComprovante?: { registrado: false; motivo: string };
}

export const MOTIVO_SEM_COMPROVANTE = 'envio anterior ao comprovante, conteúdo não registrado';

/** Os seis caminhos de CAMINHOS_DE_ENVIO no backend. Desconhecido aparece cru, não some. */
const CAMINHOS: Record<string, string> = {
  individual: 'envio individual',
  lote: 'envio em lote',
  cobranca: 'cobrança',
  cobranca_lote: 'cobrança em lote',
  correcao_boleto: 'correção de boleto',
  geracao_e_envio: 'gerar e enviar',
};
export const rotuloDoCaminho = (c?: string | null) => (c ? CAMINHOS[c] || c : '—');

/** 219136 → "214 KB"; 1153433 → "1,1 MB". */
export function fmtTamanho(bytes: number | null | undefined): string {
  const b = Number(bytes) || 0;
  if (b <= 0) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
}

export type AcaoDoAnexo = 'fiscal' | 'nenhuma';

export interface LeituraDoAnexo {
  /** O que o clique no nome faz. */
  acao: AcaoDoAnexo;
  /** Uma linha honesta sobre onde o arquivo está hoje. */
  estado: string;
  tom: 'ok' | 'aviso' | 'neutro';
  /** O arquivo de hoje tem outro tamanho: foi substituído depois do envio. */
  divergente: boolean;
}

export function lerAnexo(a: AnexoDoComprovante): LeituraDoAnexo {
  const atual = a.atual;
  if (a.origem?.tipo === 'gerado_no_envio') {
    return {
      acao: 'nenhuma',
      estado: 'gerado no envio, não armazenado: reabrir exige regerar, e o resultado pode diferir do que saiu',
      tom: 'neutro',
      divergente: false,
    };
  }
  if (atual && atual.existe === false) {
    return { acao: 'nenhuma', estado: 'não existe mais do nosso lado; fica o registro de que foi enviado', tom: 'aviso', divergente: false };
  }
  const divergente = !!atual && atual.tamanho != null && Number(atual.tamanho) !== Number(a.tamanho);
  const acao: AcaoDoAnexo = a.origem?.tipo === 'documento_fiscal' ? 'fiscal' : 'nenhuma';
  if (divergente) {
    return {
      acao,
      estado: `o arquivo de hoje tem ${fmtTamanho(atual!.tamanho)}, e o enviado tinha ${fmtTamanho(a.tamanho)}: foi substituído depois do envio`,
      tom: 'aviso',
      divergente: true,
    };
  }
  if (acao === 'fiscal') return { acao, estado: atual ? 'documento fiscal, mesmo tamanho do enviado' : 'documento fiscal', tom: 'ok', divergente: false };
  return { acao, estado: 'no armazenamento do HUB', tom: 'neutro', divergente: false };
}

/** Os comprovantes do mais recente para o mais antigo. */
export const ordenarComprovantes = (cs: Comprovante[]) =>
  [...(cs || [])].filter((c) => c.registrado).sort((a, b) => String(b.enviadoEm || '').localeCompare(String(a.enviadoEm || '')));
